import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Stethoscope, Play, Square, PlayCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CL_TZ = 'America/Santiago';

const formatChileHHmm = (isoOrDate) => {
  try {
    let d;
    if (!isoOrDate) {
      d = new Date();
    } else if (typeof isoOrDate === 'string') {
      // Backend serializa datetime UTC como ISO naive (sin offset).
      // Si el string no trae 'Z' ni offset ±HH:MM, lo tratamos como UTC.
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoOrDate);
      d = new Date(hasTz ? isoOrDate : `${isoOrDate}Z`);
    } else {
      d = new Date(isoOrDate);
    }
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: CL_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return '';
  }
};

/**
 * ClinicalNotesDialog — "Gestión Clínica" unificada para enfermeras/pabellón.
 *
 * Estructura:
 *  1) Arriba  → Botones Iniciar / Finalizar
 *  2) Centro  → Hora Inicio / Hora Término (editables)
 *  3) Abajo   → Alergias / Observaciones
 *
 * IMPORTANTE:
 *  - No valida pagos Medilink ni bloquea por estado. Solo actualiza datos
 *    clínicos y horarios. El botón Finalizar transiciona a "Operado".
 *  - Usa shadcn Dialog. Bloquea cierre por click fuera / ESC para evitar
 *    pérdidas accidentales de datos mientras la enfermera edita.
 */
export function ClinicalNotesDialog({ surgery, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    allergies: '',
    observations: '',
    time: '',
    end_time: '',
  });
  const [saving, setSaving] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [endedAt, setEndedAt] = useState(null);

  useEffect(() => {
    if (surgery) {
      setForm({
        allergies: surgery.allergies || '',
        observations: surgery.observations || '',
        time: surgery.time || '',
        end_time: surgery.end_time || '',
      });
      setStartedAt(surgery.started_at || null);
      setEndedAt(surgery.ended_at || null);
    }
  }, [surgery]);

  const handleOpenChange = (nextOpen) => {
    // Solo permitimos cerrar via botones internos (Cancelar/Guardar).
    // Radix llama onOpenChange(false) al hacer click en la X del contenido,
    // en Escape y click fuera; los dos últimos ya vienen bloqueados por
    // onInteractOutside/onEscapeKeyDown, así que aquí solo respetamos el
    // cierre por botón X.
    if (!nextOpen) onClose?.();
  };

  const nowChileHHmm = () => formatChileHHmm(new Date());

  const persistClinical = async (patch) => {
    // Actualiza SOLO campos clínicos/horarios. No toca pagos ni estado.
    await axios.patch(
      `${API_URL}/api/surgeries/${surgery.id}/clinical-notes`,
      patch,
      { withCredentials: true }
    );
  };

  const handleStart = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/surgeries/${surgery.id}/start`,
        {},
        { withCredentials: true }
      );
      const nowHHmm = nowChileHHmm();
      // Auto-completa Hora Inicio con hora actual (Chile) y persiste
      await persistClinical({ time: nowHHmm });
      setForm((f) => ({ ...f, time: nowHHmm }));
      setStartedAt(res.data?.started_at || new Date().toISOString());
      setEndedAt(null);
      toast.success('Cirugía iniciada');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar la cirugía');
    }
  };

  const handleEnd = async () => {
    if (!window.confirm('¿Marcar esta cirugía como finalizada? Se pasará a estado "Operado".')) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/surgeries/${surgery.id}/end`,
        {},
        { withCredentials: true }
      );
      const nowHHmm = nowChileHHmm();
      await persistClinical({ end_time: nowHHmm });
      setForm((f) => ({ ...f, end_time: nowHHmm }));
      setEndedAt(res.data?.ended_at || new Date().toISOString());
      toast.success('Cirugía finalizada');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al finalizar la cirugía');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistClinical({
        allergies: form.allergies,
        observations: form.observations,
        time: form.time,
        end_time: form.end_time,
      });
      toast.success('Gestión clínica actualizada');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const inProgress = startedAt && !endedAt;
  const finished = !!endedAt;

  if (!surgery) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="clinical-notes-dialog"
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1C18]">
            <Stethoscope className="w-5 h-5 text-[#2B4C3B]" />
            Gestión Clínica
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Paciente */}
          <div className="bg-[#FDFBF7] border border-[#E8E5DD] rounded-lg p-3">
            <p className="text-xs text-[#6A736B] uppercase tracking-wider font-semibold">Paciente</p>
            <p className="text-base font-medium text-[#1A1C18] mt-1">{surgery.patient_name}</p>
            <p className="text-xs text-[#6A736B]">{surgery.surgery_type}</p>
          </div>

          {/* 1) BOTONES ARRIBA — Iniciar / Finalizar */}
          <div className="flex items-center gap-2">
            {!startedAt && !endedAt && (
              <Button
                onClick={handleStart}
                data-testid="clinical-start-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Play className="h-4 w-4 mr-1.5" fill="currentColor" />
                Iniciar cirugía
              </Button>
            )}
            {inProgress && (
              <>
                <div
                  className="flex-1 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md"
                  data-testid="clinical-in-progress"
                >
                  <PlayCircle className="h-4 w-4 animate-pulse" />
                  En curso desde {formatChileHHmm(startedAt)}
                </div>
                <Button
                  onClick={handleEnd}
                  data-testid="clinical-end-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Square className="h-4 w-4 mr-1.5" fill="currentColor" />
                  Finalizar
                </Button>
              </>
            )}
            {finished && (
              <div
                className="flex-1 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-md"
                data-testid="clinical-finished"
              >
                <Square className="h-4 w-4" fill="currentColor" />
                Finalizada a las {formatChileHHmm(endedAt)}
              </div>
            )}
          </div>

          {/* 2) CENTRO — Hora Inicio / Hora Término */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="time-input">Hora Inicio</Label>
              <Input
                id="time-input"
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="clinical-time-input"
              />
            </div>
            <div>
              <Label htmlFor="end-time-input">Hora Término</Label>
              <Input
                id="end-time-input"
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="clinical-end-time-input"
              />
            </div>
          </div>

          {/* 3) ABAJO — Alergias / Observaciones */}
          <div>
            <Label htmlFor="allergies-input">Alergias</Label>
            <Textarea
              id="allergies-input"
              value={form.allergies}
              onChange={(e) => setForm({ ...form, allergies: e.target.value })}
              placeholder="Ej: Penicilina, látex..."
              rows={3}
              data-testid="clinical-notes-allergies"
            />
          </div>

          <div>
            <Label htmlFor="observations-input">Observaciones</Label>
            <Textarea
              id="observations-input"
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
              placeholder="Notas del turno, indicaciones..."
              rows={3}
              data-testid="clinical-notes-observations"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="clinical-notes-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="clinical-notes-save"
            className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
          >
            <Save size={14} className="mr-1.5" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
